<div class="preview-container">
  <h2 matTooltip="Diese Vorschau zeigt, wie die Klinikseite in der NeoDiary App aussieht.">Vorschau</h2>
  <p class="preview-hint">Änderungen werden erst mit Klick auf Speichern veröffentlicht.</p>
  <div class="phone-wrapper">
  <div class="phone-mockup">
    <!-- Status Bar -->
    <div class="status-bar">
      <span class="time">9:41</span>
      <div class="status-icons">
        <mat-icon class="status-icon">signal_cellular_alt</mat-icon>
        <mat-icon class="status-icon">wifi</mat-icon>
        <mat-icon class="status-icon">battery_full</mat-icon>
      </div>
    </div>

    <!-- Navigation Bar -->
    <div class="nav-bar">
      <mat-icon class="back-arrow">chevron_left</mat-icon>
      <span class="clinic-name">{{ clinic?.name }}</span>
      <span class="nav-spacer"></span>
    </div>

    <!-- Scrollbarer Inhaltsbereich -->
    <div class="phone-content">
      @if (publishedBlocks.length === 0) {
        <div class="empty-state">
          <mat-icon class="empty-icon">visibility_off</mat-icon>
          <p class="empty-title">Keine Inhalte</p>
          <p class="empty-text">Es sind noch keine veröffentlichten Inhalte vorhanden. Erstelle Blöcke im Editor und aktiviere den Schalter, um sie hier zu sehen.</p>
        </div>
      }
      @for (block of publishedBlocks; track block.key) {
        @if (block.is_collapsible) {
          <div class="preview-collapsible"
               [class.expanded]="isExpanded(block)"
               matTooltip="Dieser Block ist in der App einklappbar. Tippen öffnet bzw. schließt ihn.">
            <div class="collapsible-header" (click)="toggleExpanded(block, $event)">
              <span class="collapsible-title">{{ collapsibleHeader(block) }}</span>
              <mat-icon class="collapsible-chevron">{{ isExpanded(block) ? 'expand_less' : 'expand_more' }}</mat-icon>
            </div>
            @if (isExpanded(block)) {
              <div class="collapsible-body">
                <ng-container *ngTemplateOutlet="blockContent; context: { $implicit: block }"></ng-container>
              </div>
            }
          </div>
        } @else {
          <ng-container *ngTemplateOutlet="blockContent; context: { $implicit: block }"></ng-container>
        }
      }

      <ng-template #blockContent let-block>
        @switch (block.block_type.name) {
          @case ('logo') {
            <div class="preview-logo" matTooltip="Das Logo der Klinik wird hier angezeigt.">
              @if (getMediaUrl(block.logo_path ?? clinic?.logo_path ?? null) && !hasImageError(block.logo_path ?? clinic?.logo_path ?? null)) {
                <img [src]="getMediaUrl(block.logo_path ?? clinic?.logo_path ?? null)" alt="Logo"
                     (error)="onImageError(block.logo_path ?? clinic?.logo_path ?? null, $event)">
              } @else if (hasImageError(block.logo_path ?? clinic?.logo_path ?? null)) {
                <div class="preview-image-error" [matTooltip]="imageErrorUrl(block.logo_path ?? clinic?.logo_path ?? null)">
                  <mat-icon>broken_image</mat-icon>
                </div>
              } @else {
                <div class="logo-placeholder">
                  <mat-icon>image</mat-icon>
                </div>
              }
            </div>
          }
          @case ('address') {
            <div class="preview-address" matTooltip="Wenn der Nutzer hier tippt, wird die Adresse in einer Karten-App geöffnet.">
              <p>{{ block.street ?? clinic?.street }}</p>
              <p>{{ (block.postal_code ?? clinic?.postal_code) + ' ' + (block.city ?? clinic?.city) }}</p>
            </div>
          }
          @case ('phone') {
            <div class="preview-button-row" matTooltip="Wenn der Nutzer hier tippt, wird die Rufnummer angerufen.">
              <mat-icon>phone</mat-icon>
              <span>{{ block.phone ?? clinic?.phone }}</span>
            </div>
          }
          @case ('website') {
            <div class="preview-button-row" matTooltip="Wenn der Nutzer hier tippt, wird die Website im Browser geöffnet.">
              <mat-icon>language</mat-icon>
              <span>Webseite</span>
            </div>
          }
          @case ('text') {
            <div class="preview-text" matTooltip="Dieser Textblock wird dem Nutzer als Fließtext angezeigt.">
              @if (block.title) { <h3>{{ block.title }}</h3> }
              <p>{{ block.body_text }}</p>
            </div>
          }
          @case ('image') {
            <div class="preview-image" matTooltip="Dieses Bild wird dem Nutzer in voller Breite angezeigt.">
              @if (block.title) { <h3>{{ block.title }}</h3> }
              @if (getMediaUrl(block.image_path) && !hasImageError(block.image_path)) {
                <img [src]="getMediaUrl(block.image_path)" alt="Bild"
                     (error)="onImageError(block.image_path, $event)">
              } @else if (hasImageError(block.image_path)) {
                <div class="preview-image-error" [matTooltip]="imageErrorUrl(block.image_path)">
                  <mat-icon>broken_image</mat-icon>
                  <span>Bild konnte nicht geladen werden</span>
                </div>
              }
              @if (block.body_text) { <p>{{ block.body_text }}</p> }
            </div>
          }
          @case ('video') {
            <div class="preview-video" matTooltip="Wenn der Nutzer hier tippt, wird das Video abgespielt.">
              @if (block.title) { <h3>{{ block.title }}</h3> }
              <div class="video-thumbnail">
                @if (getMediaUrl(block.video_preview_path) && !hasImageError(block.video_preview_path)) {
                  <img [src]="getMediaUrl(block.video_preview_path)" alt="Video"
                       (error)="onImageError(block.video_preview_path, $event)">
                } @else {
                  <div class="video-placeholder">
                    <mat-icon>{{ hasImageError(block.video_preview_path) ? 'broken_image' : 'videocam' }}</mat-icon>
                  </div>
                }
                <div class="play-overlay">
                  <mat-icon>play_circle</mat-icon>
                </div>
              </div>
              @if (block.body_text) { <p>{{ block.body_text }}</p> }
            </div>
          }
          @case ('button') {
            <div class="preview-custom-button" matTooltip="Wenn der Nutzer hier tippt, wird der hinterlegte Link geöffnet.">
              <span>{{ block.button_text }}</span>
            </div>
          }
          @case ('contact_person') {
            <div class="preview-contact" matTooltip="Die Kontaktdaten der Ansprechperson. Der Nutzer kann Rufnummer und E-Mail direkt antippen.">
              @if (block.contact_name) { <h3>{{ block.contact_name }}</h3> }
              @if (block.contact_function) { <p class="contact-function">{{ block.contact_function }}</p> }
              @if (block.contact_area) { <p class="contact-area">{{ block.contact_area_custom || block.contact_area }}</p> }
              @if (block.contact_phone) { <p>{{ block.contact_phone }}</p> }
              @if (block.contact_email) { <p>{{ block.contact_email }}</p> }
              @if (block.contact_note) { <p class="contact-note">{{ block.contact_note }}</p> }
            </div>
          }
        }
      </ng-template>
    </div>

    <!-- Home Indicator -->
    <div class="home-indicator"></div>
  </div>
  </div>
</div>
